import React from 'react';
import { ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { Paprika } from '../../styles/theme';
import { BrandRow } from './BrandRow';

export const ONBOARDING_DESKTOP_MIN = 1024;

interface Props {
  /** Small caps line at the top of the desktop rail. */
  kicker: string;
  /** The rail's content on desktop; stacked above the pane on phone. */
  rail: React.ReactNode;
  /** The screen's main content. */
  children: React.ReactNode;
  /** Pinned under the pane on phone (progress footer, confirm button). */
  footer?: React.ReactNode;
  /** Pinned under the rail on desktop instead of the pane. */
  railFooter?: React.ReactNode;
  scroll?: boolean;
  /** End of the brand row, on both layouts. */
  brandTrailing?: React.ReactNode;
}

/**
 * The shell shared by the three onboarding screens: brand row, then on phone
 * a single column; on desktop (≥ 1024) a 376 px sunk rail on the left holding
 * the brand, the kicker and whatever Du Chef says, and the pane on the right.
 */
export function OnboardingFrame({ kicker, rail, children, footer, railFooter, scroll = false, brandTrailing }: Props) {
  const { width } = useWindowDimensions();
  const desktop = width >= ONBOARDING_DESKTOP_MIN;

  if (desktop) {
    return (
      <View style={styles.desktop} testID="onb-frame-desktop">
        <View style={styles.rail}>
          <BrandRow variant="rail" trailing={brandTrailing} />
          <Text style={styles.kicker}>{kicker}</Text>
          <View style={styles.railBody}>{rail}</View>
          {railFooter ?? footer}
        </View>
        <View style={styles.pane}>
          {scroll ? <ScrollView contentContainerStyle={styles.paneScroll}>{children}</ScrollView> : <View style={styles.paneFlex}>{children}</View>}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.phone} testID="onb-frame-phone">
      <BrandRow variant="phone" trailing={brandTrailing} />
      <View style={styles.phoneRail}>{rail}</View>
      {scroll ? (
        <ScrollView style={styles.phoneScroll} contentContainerStyle={styles.phoneScrollContent} keyboardShouldPersistTaps="handled">{children}</ScrollView>
      ) : (
        <View style={styles.phoneBody}>{children}</View>
      )}
      {footer ? <View style={styles.phoneFooter}>{footer}</View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  phone: { flex: 1, backgroundColor: Paprika.background, paddingHorizontal: 18, paddingTop: 14, paddingBottom: 12, gap: 12 },
  phoneRail: { gap: 12 },
  phoneBody: { flex: 1, minHeight: 0 },
  phoneScroll: { flex: 1 },
  phoneScrollContent: { gap: 12, paddingBottom: 16 },
  phoneFooter: { paddingTop: 4 },
  desktop: { flex: 1, flexDirection: 'row', backgroundColor: Paprika.background },
  rail: { width: 376, backgroundColor: Paprika.bgSunk, paddingHorizontal: 30, paddingVertical: 28, gap: 18 },
  railBody: { flex: 1, gap: 16 },
  kicker: { fontFamily: 'Inter-SemiBold', fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase', color: Paprika.accent },
  pane: { flex: 1, minWidth: 0 },
  paneFlex: { flex: 1, paddingHorizontal: 34, paddingVertical: 26 },
  paneScroll: { flexGrow: 1, paddingHorizontal: 34, paddingVertical: 26, gap: 16 },
});
